import React, { ReactNode } from 'react';

interface IconWrapperProps {
  children: ReactNode;
  title?: string;
  size?: number;
}

export type BadgeVariant = 'neutral' | 'risky' | 'danger' | 'pending' | 'safe';

interface BadgeProps {
  size?: number;
  className?: string;
  variant: BadgeVariant;
}

const GRADIENT_CONFIGS: Record<BadgeVariant, { start: string; end: string; endOpacity: number }> = {
  neutral: {
    start: '#B7B7B7',
    end: '#C8C8C8',
    endOpacity: 1,
  },
  risky: {
    start: '#F97F12',
    end: '#F99512',
    endOpacity: 0.7,
  },
  danger: {
    start: '#F55E66',
    end: '#F55E66',
    endOpacity: 0.7,
  },
  pending: {
    start: '#B7B7B7',
    end: '#C8C8C8',
    endOpacity: 1,
  },
  safe: {
    start: '#22C55E',
    end: '#16A34A',
    endOpacity: 1,
  },
};

const IconWrapper: React.FC<IconWrapperProps> = ({ children, title, size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label={title}
    className="fill-[url(#lightGradient)] dark:fill-[url(#darkGradient)]"
  >
    <defs>
      <linearGradient
        id="darkGradient"
        x1="12.1302"
        y1="-0.700991"
        x2="7.5223"
        y2="11.0728"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#25262E" />
        <stop offset="1" stopColor="#25262E" stopOpacity="0.67" />
      </linearGradient>

      <linearGradient
        id="lightGradient"
        x1="7.75332"
        y1="-0.509204"
        x2="5.02183"
        y2="6.8293"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="white" />
        <stop offset="1" stopColor="white" stopOpacity="0.67" />
      </linearGradient>
    </defs>
    {title && <title>{title}</title>}
    {children}
  </svg>
);

const ShieldArc: React.FC<{ fill: string }> = ({ fill }) => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M7.80949 0.725132C7.93596 0.706857 8.0644 0.706857 8.19087 0.725132C8.33662 0.746194 8.47224 0.79743 8.57993 0.838113L8.60879 0.848989L12.2704 2.222C12.6823 2.37577 13.044 2.51081 13.3225 2.75629C13.5662 2.97104 13.7539 3.2419 13.8694 3.54546C14.0015 3.89247 14.0009 4.27855 14.0003 4.71817L14.0002 7.99942C14.0002 9.88384 12.9753 11.4549 11.8675 12.609C10.7522 13.7709 9.47618 14.5915 8.80144 14.9852L8.77412 15.0012C8.65099 15.0735 8.49117 15.1673 8.27975 15.2127C8.10678 15.2498 7.89357 15.2498 7.7206 15.2127C7.50919 15.1673 7.34937 15.0735 7.22624 15.0012L7.19891 14.9852C6.52417 14.5915 5.24815 13.7709 4.13288 12.609C3.02504 11.4549 2.00018 9.88384 2.00018 7.99942L2.00009 4.71818C1.99945 4.27855 1.99889 3.89247 2.13094 3.54546C2.24645 3.2419 2.43416 2.97104 2.67783 2.75629C2.95638 2.51081 3.31808 2.37577 3.72994 2.222L7.39156 0.848989L7.42042 0.838114C7.52811 0.79743 7.66373 0.746194 7.80949 0.725132Z"
    fill={fill}
  />
);

const ExclamationIcon = () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M8.73958 5.66667C8.73958 5.29848 8.44111 5 8.07292 5C7.70473 5 7.40625 5.29848 7.40625 5.66667V8C7.40625 8.36819 7.70473 8.66667 8.07292 8.66667C8.44111 8.66667 8.73958 8.36819 8.73958 8V5.66667ZM8.07292 9.66667C7.70473 9.66667 7.40625 9.96514 7.40625 10.3333C7.40625 10.7015 7.70473 11 8.07292 11H8.07958C8.44777 11 8.74625 10.7015 8.74625 10.3333C8.74625 9.96514 8.44777 9.66667 8.07958 9.66667H8.07292Z"
  />
);

export const BadgeIcon: React.FC<BadgeProps> = ({ variant, size = 16 }) => {
  const gradientId = `${variant}Gradient`;
  const config = GRADIENT_CONFIGS[variant];

  return (
    <IconWrapper size={size}>
      <defs>
        <linearGradient
          id={gradientId}
          x1="17.0004"
          y1="15.2405"
          x2="-2.05964"
          y2="2.72703"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={config.start} />
          <stop offset="1" stopColor={config.end} stopOpacity={config.endOpacity} />
        </linearGradient>
      </defs>

      <ShieldArc fill={`url(#${gradientId})`} />

      <ExclamationIcon />
    </IconWrapper>
  );
};
